import { Divider } from '@droplet-tech-code/core-elements/module/ui/Divider/Divider';
import { HStack, VStack } from '@droplet-tech-code/core-elements/module/ui/Stack';
import { View } from '@droplet-tech-code/core-elements/module/ui/View';
import {
  isNativeMobile,
  isWeb,
} from '@droplet-tech-code/core-elements/module/utils/utils.helper';
import { SupportRequestBackendClient } from '@naus-code/naus-admin-types';
import {
  BookingPaymentSession,
  BookSessionBookingError,
  PaymentSessionProductSummary,
  SupportRequestOptions,
} from '@naus-code/naus-client-types';
import React from 'react';

import { LinkText } from '~/components/LinkText';
import { AppText } from '~/components/Text';
import { translate } from '~/utils/translation.utils';

import { usePaymentSession } from '../Modals/PaymentSession';
import { RefundMethodInfo } from '../RequestOptions/RequestOptions';
import { ModifySnapshot } from '../RequestOptions/RequestOptions.ModifySnapshot';
import { Field } from '~/components/Card/Field';
import { VIRequestOptions } from '../RequestOptions/RequestOptions.VirtualInventory';
import { KoalaRequestOption } from '../RequestOptions/RequestOptions.Koala';
import { dayjs } from '@droplet-tech-code/core-elements/module/utils/utils.date';
import { bookingApi } from '~/screens/Support/Bookings/Bookings.api';
import { ProvisionalRequestOptions } from '../RequestOptions/RequestOptions.Provisional';

export const SupportRequestDataHeader = () => {
  return (
    <>
      <View
        px="2.5"
        py="3"
        bg="monochrome-extraLight"
        justify="center"
        style={{ maxWidth: isWeb ? 400 : undefined, height: '100%' }}
      >
        <AppText.MiscTag type="heading1">
          {translate('supportRequests.data')}
        </AppText.MiscTag>
      </View>
      <Divider bg="monochrome-light" style={{ maxWidth: isWeb ? 400 : undefined }} />
    </>
  );
};

export const getVIOptionsFromSR = (supportRequestInfo: SupportRequestBackendClient) => {
  if (supportRequestInfo.requestOptions?.type !== 'vi-booking') {
    return;
  }
  return {
    viPayload: supportRequestInfo.requestOptions.virtualInventoryPayload,
    errorPayload: (supportRequestInfo.requestOptions as any).virtualInventoryError as
      | VirtualInventoryErrorRequestOption[]
      | undefined,
    validUntil: supportRequestInfo.requestOptions.validUntil,
  };
};
export const getProvisionalOptionsFromSR = (
  supportRequestInfo: SupportRequestBackendClient,
) => {
  if (supportRequestInfo.requestOptions?.type !== 'provisional-booking') {
    return;
  }
  return {
    validUntil: supportRequestInfo.requestOptions.validUntil,
  };
};

// A provisional booking can only be confirmed while its held payment can still be captured,
// so it expires with the earliest hold on the booking's provisional payment sessions
export const getProvisionalPaymentValidUntil = (booking: {
  paymentSessions: PaymentSessionProductSummary[];
}) => {
  let validUntil: string | undefined;
  for (const paymentSession of booking.paymentSessions) {
    if (
      (paymentSession.type !== 'booking' &&
        paymentSession.type !== 'booking-price-change') ||
      paymentSession.status.status !== 'book-success-provisional'
    ) {
      continue;
    }
    const paymentValidUntil =
      paymentSession.paymentSummary.paymentMethod.method?.paymentValidUntil;
    if (
      paymentValidUntil &&
      (!validUntil || dayjs(paymentValidUntil).isBefore(validUntil))
    ) {
      validUntil = paymentValidUntil;
    }
  }
  return validUntil;
};

export const useProvisionalPaymentValidUntil = (
  supportRequestInfo: SupportRequestBackendClient,
) => {
  const isProvisional = !!getProvisionalOptionsFromSR(supportRequestInfo);
  const booking = bookingApi.useGetBookingQuery(supportRequestInfo.bookingId || '', {
    skip: !supportRequestInfo.bookingId || !isProvisional,
  });
  if (!isProvisional || !booking.data) {
    return;
  }
  return getProvisionalPaymentValidUntil(booking.data.booking);
};

export interface VirtualInventoryErrorRequestOption {
  type: 'confirm' | 'cancel' | 'update';
  id: string;
  virtualInventoryToken?: string;
  providerCode?: string;
  meta?: any;
  timestamp: string;
  description: string;
  isOutDated: boolean;
}

export const SupportRequestDataContent = ({
  supportRequestInfo,
  check,
}: {
  supportRequestInfo: SupportRequestBackendClient;
  check: string;
}) => {
  const { open: openPaymentSession } = usePaymentSession();
  const isBookingSession = supportRequestInfo.paymentSession?.type === 'booking';

  return (
    <VStack
      flex={isNativeMobile ? 1 : undefined}
      style={{ maxWidth: isWeb ? 400 : undefined, height: '100%' }}
    >
      <KoalaRequestOption supportRequestInfo={supportRequestInfo} check={check} />
      {supportRequestInfo.requestOptions && (
        <VStack space="2.5" px="3">
          <VStack flex={1} space="2">
            <ModifySnapshot
              options={supportRequestInfo.requestOptions as SupportRequestOptions}
            />
            <RefundMethodInfo options={supportRequestInfo.requestOptions} />
          </VStack>
          <Divider />
        </VStack>
      )}
      <VIRequestOptions supportRequestInfo={supportRequestInfo} check={check} />
      <ProvisionalRequestOptions supportRequestInfo={supportRequestInfo} check={check} />
      {supportRequestInfo.paymentSession && (
        <VStack space="2.5" px="3">
          <Field
            label={translate('customer.customerId')}
            value={supportRequestInfo._customerId}
            isCopyable
          />
          {isBookingSession && (
            <Field
              label={translate('bookings.paymentSession.errorType')}
              value={
                (
                  (supportRequestInfo.paymentSession as BookingPaymentSession)
                    .bookSession as BookSessionBookingError
                ).trips?.at(0)?.errorType || ''
              }
            />
          )}
          <HStack flex={1} justify="flex-end">
            <LinkText
              onPress={() => {
                if (supportRequestInfo.paymentSession) {
                  openPaymentSession(
                    supportRequestInfo.paymentSession,
                    supportRequestInfo.bookingId,
                  );
                }
              }}
            >
              {translate('supportRequests.paymentSession')}
            </LinkText>
          </HStack>
          <Divider />
        </VStack>
      )}
    </VStack>
  );
};
